import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ledgerFetch } from "@/lib/ledgerDemoSource";
import { useAuth } from "@/contexts/AuthContext";
import {
  AdminViewToggle,
  FriendlyMemberTable,
  FriendlyMetricsCard,
  NeedsNavCta,
  UpdateHoldingDialog,
} from "@/components/ledger/LedgerTrackA";

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

function safeFmt(v) {
  if (v == null) return "—";
  return String(v);
}

/** Render XIRR as percentage string, e.g. 0.0839 → "8.39%" */
function fmtPct(v) {
  if (v == null) return null;
  return (Number(v) * 100).toFixed(2) + "%";
}

/** Render a multiple to 4 decimal places, e.g. 1.8834 → "1.8834×" */
function fmtMultiple(v) {
  if (v == null) return null;
  return Number(v).toFixed(4) + "×";
}

function fmtAmount(amount, currency) {
  if (amount == null) return "—";
  const code = String(currency || "").trim().toUpperCase();
  const safeCode = /^[A-Z]{3}$/.test(code) ? code : "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: safeCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${safeCode} ${Number(amount).toLocaleString()}`;
  }
}

// ── Tag badges ────────────────────────────────────────────────────────────────

function NavStatusBadge({ status }) {
  if (!status) return null;
  if (status === "lp_nav") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
        lp_nav
      </span>
    );
  }
  if (status === "needs_nav") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-300">
        needs_nav
      </span>
    );
  }
  if (status === "fund_level_dropped") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400 border border-gray-200">
        fund_level_dropped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
      {status}
    </span>
  );
}

function StreamFlagBadges({ flags }) {
  if (!flags) return null;
  const parts = String(flags)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((flag) => {
        if (flag === "computed_stale_nav") {
          return (
            <span
              key={flag}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-300"
            >
              {flag}
            </span>
          );
        }
        if (flag === "needs_nav") {
          return (
            <span
              key={flag}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-300"
            >
              {flag}
            </span>
          );
        }
        if (flag === "call_timing_underdetermined") {
          return (
            <span
              key={flag}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200"
            >
              {flag}
            </span>
          );
        }
        return (
          <span
            key={flag}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200"
          >
            {flag}
          </span>
        );
      })}
    </>
  );
}

function GateRejectedBadge() {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white border border-red-700 uppercase tracking-wide">
      GATE-REJECTED
    </span>
  );
}

function DocStatusBadge({ status }) {
  if (status === "unavailable") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400 border border-gray-200">
        doc unavailable
      </span>
    );
  }
  return null;
}

// ── Tier badge ────────────────────────────────────────────────────────────────

function TierBadge({ tier }) {
  if (!tier) return null;
  if (tier === "book_verified") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded font-bold text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-400 uppercase tracking-wide">
        book_verified
      </span>
    );
  }
  if (tier === "computed_only") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded font-bold text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-300 uppercase tracking-wide">
        computed_only
      </span>
    );
  }
  if (tier === "honest_null") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded font-bold text-[11px] bg-amber-50 text-amber-700 border border-amber-400 uppercase tracking-wide">
        honest_null
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded font-bold text-[11px] bg-gray-100 text-gray-500 border border-gray-300 uppercase tracking-wide">
      {tier}
    </span>
  );
}

// ── Ratio cell ────────────────────────────────────────────────────────────────

function RatioCell({ label, value, nullReason }) {
  const hasValue = value != null;
  return (
    <div className="flex flex-col items-center min-w-[80px] px-3 py-2 rounded border border-gray-200 bg-white">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">
        {label}
      </span>
      {hasValue ? (
        <span className="text-sm font-mono font-bold text-gray-900">{value}</span>
      ) : (
        <span className="text-xs font-mono text-gray-400 italic">
          {"null"}
          {nullReason ? (
            <span className="text-gray-300"> · {nullReason}</span>
          ) : null}
        </span>
      )}
    </div>
  );
}

// ── Metrics card (per member) ─────────────────────────────────────────────────

function MetricsCard({ m }) {
  if (!m) return null;

  const xirrFmt = fmtPct(m.xirr);
  const dpiFmt = fmtMultiple(m.dpi);
  const tvpiFmt = fmtMultiple(m.tvpi);
  const rvpiFmt = fmtMultiple(m.rvpi);

  // null reasons per ratio
  const xirrNullReason = m.xirr_confidence || null;
  const tvpiNullReason = m.nav_status || null;
  const rvpiNullReason = m.nav_status || null;

  // border colour keyed by tier
  const tierBorder =
    m.tier === "book_verified"
      ? "border-emerald-300 bg-emerald-50/40"
      : m.tier === "computed_only"
      ? "border-indigo-200 bg-indigo-50/30"
      : m.tier === "honest_null"
      ? "border-amber-300 bg-amber-50/40"
      : "border-gray-200 bg-gray-50/40";

  return (
    <div className={`rounded-md border ${tierBorder} px-3 py-2.5 mb-3 flex flex-col gap-2`}>
      {/* Top row: tier + nav_status */}
      <div className="flex items-center gap-2 flex-wrap">
        <TierBadge tier={m.tier} />
        <NavStatusBadge status={m.nav_status} />
        {m.stream_flags && (
          <div className="flex flex-wrap gap-1">
            <StreamFlagBadges flags={m.stream_flags} />
          </div>
        )}
      </div>

      {/* Ratio cells row */}
      <div className="flex flex-wrap gap-2">
        <RatioCell
          label="DPI"
          value={dpiFmt}
          nullReason={m.dpi == null ? null : null}
        />
        <RatioCell
          label="TVPI"
          value={tvpiFmt}
          nullReason={m.tvpi == null ? tvpiNullReason : null}
        />
        <RatioCell
          label="RVPI"
          value={rvpiFmt}
          nullReason={m.rvpi == null ? rvpiNullReason : null}
        />

        {/* XIRR cell — wider because it may carry book_xirr note */}
        <div className="flex flex-col items-center min-w-[80px] px-3 py-2 rounded border border-gray-200 bg-white">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">
            XIRR
          </span>
          {xirrFmt != null ? (
            <div className="flex flex-col items-center">
              <span className="text-sm font-mono font-bold text-gray-900">{xirrFmt}</span>
              {m.book_xirr != null && (
                <span className="text-[10px] font-mono text-emerald-700 mt-0.5">
                  book IRR: {m.book_xirr}
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <span className="text-xs font-mono text-gray-400 italic">
                {"null"}
                {xirrNullReason ? (
                  <span className="text-gray-300"> · {xirrNullReason}</span>
                ) : null}
              </span>
              {m.book_xirr != null && (
                <span className="text-[10px] font-mono text-emerald-700 mt-0.5">
                  book IRR: {m.book_xirr}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Optional: levels as small text */}
      {(m.commitment != null || m.called != null || m.distributed != null || m.nav != null) && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] font-mono text-gray-500">
          {m.commitment != null && (
            <span>commitment {fmtAmount(m.commitment, m.currency)}</span>
          )}
          {m.called != null && (
            <span>called {fmtAmount(m.called, m.currency)}</span>
          )}
          {m.distributed != null && (
            <span>distributed {fmtAmount(m.distributed, m.currency)}</span>
          )}
          {m.nav != null && (
            <span>nav {fmtAmount(m.nav, m.currency)}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Member input panel ────────────────────────────────────────────────────────
// Provides: add_line / supply_nav (amber, when needs_nav) / assert_close.
// GUARDRAIL: no field for XIRR/TVPI/DPI/RVPI — those are computed outputs only.

function MemberInputPanel({ streamId, navStatus, currency, token, onResult }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // add_line fields
  const [alDate, setAlDate] = useState("");
  const [alType, setAlType] = useState("capital_call_event_amount");
  const [alAmount, setAlAmount] = useState("");
  const [alCcy, setAlCcy] = useState(currency || "USD");

  // supply_nav fields (shown only when navStatus === "needs_nav")
  const [navDate, setNavDate] = useState("");
  const [navAmt, setNavAmt] = useState("");

  const needsNav = navStatus === "needs_nav";

  async function postInput(body) {
    setSubmitting(true);
    setErr(null);
    try {
      const res = await ledgerFetch("/api/ledger/poc-inspection/input", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ stream_id: streamId, ...body }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ": " + txt.slice(0, 120) : ""}`);
      }
      const json = await res.json();
      onResult(json);
      return json;
    } catch (e) {
      setErr(e?.message || "request failed");
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddLine(e) {
    e.preventDefault();
    if (!alDate || !alAmount) return;
    const ok = await postInput({
      action: "add_line",
      event_date: alDate,
      type: alType,
      amount: parseFloat(alAmount),
      currency: alCcy || undefined,
    });
    if (ok) {
      setAlDate("");
      setAlAmount("");
    }
  }

  async function handleSupplyNav(e) {
    e.preventDefault();
    if (!navDate || !navAmt) return;
    const ok = await postInput({
      action: "supply_nav",
      event_date: navDate,
      amount: parseFloat(navAmt),
      currency: currency || undefined,
    });
    if (ok) {
      setNavDate("");
      setNavAmt("");
    }
  }

  function handleAssertClose() {
    if (
      !window.confirm(
        "Assert this position as closed? This adds a realized marker. Reversible via discard."
      )
    )
      return;
    postInput({
      action: "assert_close",
      event_date: new Date().toISOString().slice(0, 10),
    });
  }

  const inputCls =
    "h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300";

  return (
    <div className="mb-3">
      {/* Supply NAV — prominent amber banner, always visible when nav is missing */}
      {needsNav && (
        <div className="mb-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2">
          <p className="text-xs font-semibold text-amber-800 mb-1.5">
            NAV missing — supply a NAV value to unlock TVPI / RVPI
          </p>
          <form onSubmit={handleSupplyNav} className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={navDate}
              onChange={(e) => setNavDate(e.target.value)}
              className={inputCls}
              required
              disabled={submitting}
            />
            <input
              type="number"
              value={navAmt}
              onChange={(e) => setNavAmt(e.target.value)}
              placeholder={`NAV amount (${currency || "USD"})`}
              className={`${inputCls} w-44`}
              step="any"
              min="0"
              required
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting || !navDate || !navAmt}
              className="h-7 px-3 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? "…" : "Supply NAV"}
            </button>
          </form>
        </div>
      )}

      {/* Edit inputs toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
      >
        <span className="font-mono text-[10px]">{open ? "▲" : "▼"}</span>
        Edit inputs
      </button>

      {open && (
        <div className="mt-2 rounded-md border border-blue-200 bg-blue-50/20 px-3 py-3 space-y-3">
          {/* Add line */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
              Add line
            </p>
            <form onSubmit={handleAddLine} className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={alDate}
                onChange={(e) => setAlDate(e.target.value)}
                className={inputCls}
                required
                disabled={submitting}
              />
              <select
                value={alType}
                onChange={(e) => setAlType(e.target.value)}
                className={`${inputCls} pr-1`}
                disabled={submitting}
              >
                <option value="capital_call_event_amount">Capital call</option>
                <option value="distribution_event_amount">Distribution</option>
              </select>
              <input
                type="number"
                value={alAmount}
                onChange={(e) => setAlAmount(e.target.value)}
                placeholder="Amount"
                className={`${inputCls} w-32`}
                step="any"
                required
                disabled={submitting}
              />
              <input
                type="text"
                value={alCcy}
                onChange={(e) => setAlCcy(e.target.value)}
                placeholder="CCY"
                className={`${inputCls} w-14`}
                maxLength={3}
                disabled={submitting}
              />
              <button
                type="submit"
                disabled={submitting || !alDate || !alAmount}
                className="h-7 px-3 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? "…" : "Add line"}
              </button>
            </form>
          </div>

          {/* Assert close */}
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
              Assert close
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAssertClose}
                disabled={submitting}
                className="h-7 px-3 rounded text-xs font-medium border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 transition-colors"
              >
                Assert position closed
              </button>
              <span className="text-[10px] text-gray-400">
                Adds realized marker · reversible via discard
              </span>
            </div>
          </div>

          {err && (
            <p className="text-xs text-red-600 font-mono bg-red-50 border border-red-200 rounded px-2 py-1">
              {err}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Per-member table ──────────────────────────────────────────────────────────

function MemberTable({ rows, token, clientInputs, onDiscard }) {
  const [discardingId, setDiscardingId] = useState(null);

  async function handleDiscard(id) {
    setDiscardingId(id);
    try {
      const res = await ledgerFetch(`/api/ledger/poc-inspection/input/${id}/discard`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (onDiscard) onDiscard(json);
    } catch (e) {
      console.error("Discard failed:", e);
    } finally {
      setDiscardingId(null);
    }
  }

  // Merge extracted + client rows and sort by event_date, so a client-supplied line lands in the
  // ledger at its real date (not appended at the bottom). Stable within a date (extracted before client).
  const mergedRows = [
    ...rows.map((row, i) => ({ kind: "ext", row, key: `r-${i}`, date: row.event_date || "" })),
    ...(clientInputs || []).map((ci) => ({ kind: "ci", ci, key: `ci-${ci.id}`, date: ci.event_date || "" })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs font-mono">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-300">
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">event_date</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">type</th>
            <th className="px-2 py-1.5 text-right font-semibold text-gray-600 whitespace-nowrap">amount</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">currency</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">scope</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">basis</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">doc_id</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">event_id</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">stream_id</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">source_ref</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">nav_status</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">stream_flags</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">row_class</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">gate_reason</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">doc_status</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">seq</th>
            <th className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">doc</th>
          </tr>
        </thead>
        <tbody>
          {mergedRows.map((item, idx) => {
            if (item.kind === "ci") {
              const ci = item.ci;
              return (
                <tr
                  key={item.key}
                  className="border-b border-blue-200 bg-blue-50/50"
                  style={{ borderLeft: "3px solid #3b82f6" }}
                >
                  <td className="px-2 py-1 whitespace-nowrap text-blue-700 font-medium">{fmtDate(ci.event_date)}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-700">{ci.type || "—"}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-right text-blue-700">{fmtAmount(ci.amount, ci.currency)}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-600">{ci.currency || "—"}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-500 font-medium">client</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-500">{ci.nav_source ? `client_input · nav_source=${ci.nav_source}` : (ci.note || "client_input")}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-400 max-w-[120px] truncate" title={ci.stream_id || ""}>{ci.stream_id || "—"}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-300">client_input</span></td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-400">{ci.status || "active"}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-blue-300">—</td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <button type="button" onClick={() => handleDiscard(ci.id)} disabled={discardingId === ci.id} className="text-[10px] text-red-600 hover:text-red-800 underline disabled:opacity-50 transition-colors">
                      {discardingId === ci.id ? "…" : "discard"}
                    </button>
                  </td>
                </tr>
              );
            }
            const row = item.row;
            const isRejected = row.row_class === "gate_rejected";
            const rowBg = isRejected ? "bg-red-50" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/50";
            const canViewDoc =
              row.doc_status === "resolved" && row.resolved_blob_path;
            const docUnavailable = row.doc_status === "unavailable";

            return (
              <tr key={item.key} className={`border-b border-gray-100 ${rowBg}`}>
                <td className="px-2 py-1 whitespace-nowrap text-gray-700">
                  {fmtDate(row.event_date)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-700">
                  {safeFmt(row.type)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-right text-gray-700">
                  {fmtAmount(row.amount, row.currency)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                  {safeFmt(row.currency)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                  {safeFmt(row.scope)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                  {safeFmt(row.basis)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500 max-w-[120px] truncate" title={row.doc_id || ""}>
                  {safeFmt(row.doc_id)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500 max-w-[120px] truncate" title={row.event_id || ""}>
                  {safeFmt(row.event_id)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500 max-w-[120px] truncate" title={row.stream_id || ""}>
                  {safeFmt(row.stream_id)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500 max-w-[120px] truncate" title={row.source_ref || ""}>
                  {safeFmt(row.source_ref)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  <NavStatusBadge status={row.nav_status} />
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    <StreamFlagBadges flags={row.stream_flags} />
                  </div>
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {isRejected ? (
                    <GateRejectedBadge />
                  ) : (
                    <span className="text-gray-500">{safeFmt(row.row_class)}</span>
                  )}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500 max-w-[160px] truncate" title={row.gate_reason || ""}>
                  {safeFmt(row.gate_reason)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {docUnavailable ? (
                    <DocStatusBadge status="unavailable" />
                  ) : (
                    <span className="text-gray-500">{safeFmt(row.doc_status)}</span>
                  )}
                </td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500">
                  {safeFmt(row.seq)}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {canViewDoc ? (
                    <a
                      href={`/api/documents/pdf/${encodeURIComponent(row.resolved_blob_path)}?token=${token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                    >
                      <FileText className="w-3 h-3" />
                      view doc
                    </a>
                  ) : docUnavailable ? (
                    <DocStatusBadge status="unavailable" />
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Summary chip ──────────────────────────────────────────────────────────────

function SummaryChip({ label, value, variant = "default" }) {
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    amber: "bg-amber-50 text-amber-700 border-amber-300",
    red: "bg-red-50 text-red-700 border-red-300",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <div className={`inline-flex flex-col items-center px-3 py-1.5 rounded border ${styles[variant]}`}>
      <span className="text-[10px] uppercase tracking-wider font-medium opacity-70">{label}</span>
      <span className="text-sm font-mono font-semibold">{value}</span>
    </div>
  );
}

// ── Merge a projection response into the MetricsCard `m` shape ───────────────
// proj is the `projection` field from POST /input response.
// staticMetrics is the original metrics row (carries tier, book_xirr).
// Output-only metrics (XIRR/TVPI/DPI/RVPI) come ONLY from the backend computation.

function mergeProjection(proj, staticMetrics) {
  if (!proj) return staticMetrics;
  return {
    ...(staticMetrics || {}),
    dpi: proj.dpi,
    tvpi: proj.tvpi,
    rvpi: proj.rvpi,
    xirr: proj.xirr,
    xirr_confidence: proj.xirr_confidence,
    nav_status: proj.nav_status,
    stream_flags: Array.isArray(proj.flags) ? proj.flags.join(",") : (proj.flags || ""),
    commitment: proj.commitment,
    called: proj.called,
    distributed: proj.distributed,
    nav: proj.nav,
    currency: proj.currency,
    // always preserve tier and book_xirr from static (not recomputed)
    tier: staticMetrics?.tier,
    book_xirr: staticMetrics?.book_xirr,
  };
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LedgerInspectionPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // ── rawMode — admin-only; reads ?raw=1 from URL or localStorage flag ──────
  // NOTE: read from window.location.search (NOT useSearchParams) so the test
  // harness stays router-free (no <BrowserRouter> wrapper needed).
  const [rawMode, setRawMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const adminFlag = user?.role === "admin";
    if (!adminFlag) return false;
    return (
      window.location.search.includes("raw=1") ||
      localStorage.getItem("ledger_inspection_raw") === "1"
    );
  });

  // Sync rawMode if auth resolves after initial render (real app, not tests)
  useEffect(() => {
    if (!isAdmin) { setRawMode(false); return; }
    const fromUrl = window.location.search.includes("raw=1");
    const fromStorage = localStorage.getItem("ledger_inspection_raw") === "1";
    setRawMode(fromUrl || fromStorage);
  }, [isAdmin]);

  function handleToggleRaw(checked) {
    // Non-admins can never reach here (AdminViewToggle is gated), but guard anyway
    if (!isAdmin) return;
    setRawMode(checked);
    try {
      if (checked) {
        localStorage.setItem("ledger_inspection_raw", "1");
      } else {
        localStorage.removeItem("ledger_inspection_raw");
      }
    } catch {
      // localStorage may be unavailable (private mode, etc.)
    }
  }

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");
  // Per-member edit state keyed by stream_id.
  // Shape: { [stream_id]: { clientInputs: [], liveProjection: null } }
  const [memberEdits, setMemberEdits] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ledgerFetch("/api/ledger/poc-inspection", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "fetch failed");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="w-full min-w-0 space-y-4 p-4">
        <Skeleton className="h-9 w-72 rounded" />
        <Skeleton className="h-6 w-full rounded" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  // ── Error / table_present === false ──
  if (error || !data || !data.table_present) {
    return (
      <div className="w-full min-w-0 p-4">
        <Card className="border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <FileText className="w-12 h-12 text-gray-300" />
            <p className="text-base font-medium text-gray-700">
              Inspection mirror not loaded in this environment
            </p>
            {error && (
              <p className="text-xs text-gray-400 font-mono">{error}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, load_run_id, rows, metrics } = data;

  // ── Build metrics lookup: (fund_slug + "|" + investor) → metrics row ──────
  const metricsMap = new Map();
  for (const m of metrics || []) {
    const key = `${m.fund_slug}|${m.investor}`;
    metricsMap.set(key, m);
  }

  // ── Group rows: portal+fund_slug → investor → [rows] (preserve API order) ──
  const fundGroups = new Map(); // key: `${portal} / ${fund_slug}` → { fund_name, portal, fund_slug, members: Map }
  for (const row of rows || []) {
    const fundKey = `${row.portal} / ${row.fund_slug}`;
    if (!fundGroups.has(fundKey)) {
      fundGroups.set(fundKey, {
        fundKey,
        fund_name: row.fund_name,
        portal: row.portal,
        fund_slug: row.fund_slug,
        members: new Map(),
      });
    }
    const fg = fundGroups.get(fundKey);
    if (!fg.members.has(row.investor)) {
      fg.members.set(row.investor, []);
    }
    fg.members.get(row.investor).push(row);
  }

  // ── Optional filter ──
  const lc = filterText.trim().toLowerCase();
  const visibleFunds = [...fundGroups.values()].filter((fg) => {
    if (!lc) return true;
    return (
      fg.fund_name?.toLowerCase().includes(lc) ||
      fg.portal?.toLowerCase().includes(lc) ||
      fg.fund_slug?.toLowerCase().includes(lc) ||
      [...fg.members.keys()].some((inv) => inv?.toLowerCase().includes(lc))
    );
  });

  return (
    <div className="w-full min-w-0 space-y-4">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {rawMode ? (
              <>
                <h1 className="text-2xl font-bold text-gray-900">Ledger POC Inspection</h1>
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-semibold">
                  preview
                </Badge>
              </>
            ) : (
              <h1 className="text-2xl font-bold text-gray-900">Portfolio Activity</h1>
            )}
          </div>
          {/* AdminViewToggle — visible only to admins */}
          {isAdmin && (
            <AdminViewToggle rawMode={rawMode} onChange={handleToggleRaw} />
          )}
        </div>
        {rawMode && (
          <p className="text-sm text-gray-500 mt-1">
            READ-ONLY inspection mirror (preview) — raw ledger lines exactly as stored, not the computed
            dashboard. No computation, no netting, no collapsing.
          </p>
        )}
      </div>

      {/* ── Raw-only: inspection banner + summary strip ─────────────────────── */}
      {rawMode && (
        <>
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 font-medium">
            READ-ONLY inspection mirror (preview) — raw ledger lines exactly as stored, not the computed
            dashboard.
          </div>

          <div className="flex flex-wrap gap-3 items-start">
            <SummaryChip label="event_rows" value={summary?.event_rows ?? "—"} variant="indigo" />
            <SummaryChip label="gate_rejected" value={summary?.gate_rejected ?? "—"} variant={summary?.gate_rejected > 0 ? "red" : "default"} />
            <SummaryChip label="docs_resolved" value={summary?.docs_resolved ?? "—"} variant="default" />
            <SummaryChip label="docs_unavailable" value={summary?.docs_unavailable ?? "—"} variant={summary?.docs_unavailable > 0 ? "amber" : "default"} />
            {load_run_id && (
              <div className="inline-flex flex-col items-start px-3 py-1.5 rounded border border-gray-200 bg-gray-50">
                <span className="text-[10px] uppercase tracking-wider font-medium text-gray-400">load_run_id</span>
                <span className="text-xs font-mono text-gray-700">{load_run_id}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Optional filter ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Filter by fund, portal, or investor…"
          className="h-8 px-3 rounded border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 w-72"
        />
        {lc && (
          <button
            type="button"
            onClick={() => setFilterText("")}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            clear
          </button>
        )}
        <span className="text-xs text-gray-400">
          {visibleFunds.length} fund{visibleFunds.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Fund sections ───────────────────────────────────────────────────── */}
      {visibleFunds.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-2">
            <FileText className="w-10 h-10 text-gray-200" />
            <p className="text-sm text-gray-500">No rows match the current filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {visibleFunds.map((fg) => (
            <div key={fg.fundKey}>
              {/* Fund header */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h2 className="text-base font-semibold text-gray-900">{fg.fund_name || fg.fund_slug}</h2>
                <Badge variant="outline" className="text-[10px] text-gray-400 border-gray-200">
                  {fg.portal}
                </Badge>
                {rawMode && (
                  <span className="text-xs text-gray-400 font-mono">{fg.fund_slug}</span>
                )}
              </div>

              {/* Member sub-sections */}
              <div className="space-y-4 pl-2">
                {[...fg.members.entries()].map(([investor, memberRows]) => {
                  const metricsKey = `${fg.fund_slug}|${investor}`;
                  const memberMetrics = metricsMap.get(metricsKey) || null;

                  // Per-member edit state
                  const streamId = memberRows[0]?.stream_id;
                  const memberEdit = memberEdits[streamId] || { clientInputs: [], liveProjection: null };
                  const liveProj = memberEdit.liveProjection;
                  const clientInputsList = memberEdit.clientInputs || [];

                  // Handler called on every successful POST (add_line, supply_nav, assert_close, discard)
                  const onResult = (json) => {
                    setMemberEdits((prev) => ({
                      ...prev,
                      [streamId]: {
                        clientInputs: json.client_inputs || prev[streamId]?.clientInputs || [],
                        liveProjection: json.projection,
                      },
                    }));
                  };

                  // Effective nav_status comes from liveProjection if available
                  const effectiveNavStatus = liveProj
                    ? liveProj.nav_status
                    : memberMetrics?.nav_status;
                  const effectiveCurrency =
                    memberMetrics?.currency || memberRows[0]?.currency || "USD";

                  // Display metrics: static until first edit, then liveProjection merged in.
                  // XIRR/TVPI/DPI/RVPI come ONLY from backend computation — never from user input.
                  const displayMetrics = mergeProjection(liveProj, memberMetrics);

                  return (
                    <div key={investor}>
                      {/* Investor label */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          {investor || "(no investor)"}
                        </span>
                        <span className="text-[10px] text-gray-400">{memberRows.length} row{memberRows.length !== 1 ? "s" : ""}</span>
                      </div>

                      {/* ── RAW mode: original MetricsCard + MemberInputPanel + MemberTable ── */}
                      {rawMode ? (
                        <>
                          <MetricsCard m={displayMetrics} />
                          {liveProj && (
                            <div className="flex justify-end mb-2">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                recomputed · {clientInputsList.length} client input(s)
                              </span>
                            </div>
                          )}
                          <MemberInputPanel
                            streamId={streamId}
                            navStatus={effectiveNavStatus}
                            currency={effectiveCurrency}
                            token={token}
                            onResult={onResult}
                          />
                          <Card className="border-gray-200 overflow-hidden">
                            <CardContent className="p-0">
                              <MemberTable
                                rows={memberRows}
                                token={token}
                                clientInputs={clientInputsList}
                                onDiscard={onResult}
                              />
                            </CardContent>
                          </Card>
                        </>
                      ) : (
                        /* ── FRIENDLY mode: FriendlyMetricsCard + UpdateHoldingDialog + FriendlyMemberTable ── */
                        <>
                          <FriendlyMetricsCard m={displayMetrics} />
                          {liveProj && (
                            <div className="flex justify-end mb-2">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                Updated · {clientInputsList.length} item{clientInputsList.length !== 1 ? "s" : ""} added
                              </span>
                            </div>
                          )}
                          {effectiveNavStatus === "needs_nav" && <NeedsNavCta />}
                          <div className="mb-3">
                            <UpdateHoldingDialog
                              streamId={streamId}
                              currency={effectiveCurrency}
                              token={token}
                              onResult={onResult}
                            />
                          </div>
                          <Card className="border-gray-200 overflow-hidden">
                            <CardContent className="p-0">
                              <FriendlyMemberTable
                                rows={memberRows}
                                clientInputs={clientInputsList}
                              />
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
